import React, { useState, useEffect } from 'react';
import { FiSearch, FiEdit, FiTrash2, FiCheck, FiX, FiCreditCard } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const RefundAdjustments = () => {
  const [allRefunds, setAllRefunds] = useState([]);
  const [filteredRefunds, setFilteredRefunds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeStatus, setActiveStatus] = useState('all');
  const [timeRange, setTimeRange] = useState('all');
  const [refundStatuses, setRefundStatuses] = useState([]);
  const [selectedRefundStatus, setSelectedRefundStatus] = useState('all');
  const [statistics, setStatistics] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [selectedRefundDetails, setSelectedRefundDetails] = useState(null);
  const [updatedRefundData, setUpdatedRefundData] = useState({
    refund_amount: '',
    service_fee: '',
    refund_reason: '',
    admin_notes: '',
  });
  const [refundDetails, setRefundDetails] = useState(null);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const rowsPerPage = 15;
  const maxVisiblePages = 10;
  const navigate = useNavigate();

  useEffect(() => {
    // Load Razorpay script (optional, not strictly needed for refunds but kept for consistency)
    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => setRazorpayLoaded(true);
      script.onerror = () => {
        console.error('Failed to load Razorpay script');
        toast.error('Error loading payment system. Please refresh the page.');
      };
      document.body.appendChild(script);
    };

    if (!razorpayLoaded) {
      loadRazorpayScript();
    }

    fetchRefunds();
    fetchStatistics();
  }, []);

  // Helper function to mask IDs
  const maskId = (id) => {
    if (!id) return 'N/A';
    if (id.length <= 8) return id;
    return `${id.substring(0, 6)}...${id.substring(id.length - 4)}`;
  };

  // Helper function to get guest name from various possible structures
  const getGuestName = (refund) => {
    if (!refund) return 'N/A';

    // Check in booking_info (main list structure)
    if (refund.booking_info) {
      if (refund.booking_info.guest_name) return refund.booking_info.guest_name;
      if (refund.booking_info.guest_details) {
        const { firstname, lastname } = refund.booking_info.guest_details;
        if (firstname || lastname) {
          return `${firstname || ''} ${lastname || ''}`.trim();
        }
      }
    }

    // Check in booking_details (detailed view structure)
    if (refund.booking_details?.guest_details) {
      const { firstname, lastname } = refund.booking_details.guest_details;
      if (firstname || lastname) {
        return `${firstname || ''} ${lastname || ''}`.trim();
      }
    }

    return 'N/A';
  };

  // Helper function to get guest phone from various possible structures
  const getGuestPhone = (refund) => {
    if (!refund) return 'N/A';

    // Check in booking_info (main list structure)
    if (refund.booking_info) {
      if (refund.booking_info.guest_phone) return refund.booking_info.guest_phone;
      if (refund.booking_info.guest_details?.phonenumber)
        return refund.booking_info.guest_details.phonenumber;
      if (refund.booking_info.phone) return refund.booking_info.phone;
    }

    // Check in booking_details (detailed view structure)
    if (refund.booking_details?.guest_details?.phonenumber) {
      return refund.booking_details.guest_details.phonenumber;
    }

    return 'N/A';
  };

  const fetchRefunds = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/admin/refunds`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (data.success && data.refunds) {
        setAllRefunds(data.refunds);
        setFilteredRefunds(data.refunds);

        // Get unique refund statuses
        const uniqueStatuses = [
          ...new Set(data.refunds.map((item) => item.refund_status).filter(Boolean)),
        ].sort();

        setRefundStatuses(uniqueStatuses);
      }
    } catch (error) {
      console.error('Error fetching refunds:', error);
      toast.error('Failed to fetch refund data');
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/admin/refunds/statistics`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (data.success) {
        setStatistics(data.statistics);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchRefundDetails = async (refundid) => {
    try {
      setLoadingDetails(true);
      const response = await fetch(`${BQ_BASE_URL}/bq/api/admin/refunds/${refundid}`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setRefundDetails(data);
      return data;
    } catch (error) {
      console.error('Error fetching refund details:', error);
      toast.error('Failed to fetch refund details');
      return null;
    } finally {
      setLoadingDetails(false);
    }
  };

  // Function to fetch and set selected refund with details
  const fetchSelectedRefundDetails = async (refund) => {
    try {
      setLoadingDetails(true);
      const details = await fetchRefundDetails(refund.refundid);
      if (details) {
        // Merge the basic refund data with the detailed data
        const enrichedRefund = {
          ...refund,
          ...details,
          booking_details: details.booking_details || refund.booking_details,
          payment_details: details.payment_details || refund.payment_details,
        };
        setSelectedRefundDetails(enrichedRefund);
      } else {
        setSelectedRefundDetails(refund);
      }
    } catch (error) {
      console.error('Error fetching refund details:', error);
      setSelectedRefundDetails(refund);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Helper function to update refund status in local state
  const updateRefundStatus = (refundId, status, razorpayRefundId = null) => {
    setAllRefunds((prev) =>
      prev.map((item) =>
        item.refundid === refundId
          ? {
              ...item,
              refund_status: status,
              razorpay_refund_id: razorpayRefundId || item.razorpay_refund_id,
              updatedate: new Date().toISOString(),
            }
          : item
      )
    );

    setFilteredRefunds((prev) =>
      prev.map((item) =>
        item.refundid === refundId
          ? {
              ...item,
              refund_status: status,
              razorpay_refund_id: razorpayRefundId || item.razorpay_refund_id,
              updatedate: new Date().toISOString(),
            }
          : item
      )
    );
  };

  const handleInitiateRefund = async () => {
    if (!selectedRefund) return;

    setProcessing(true);

    try {
      // Use refund_amount instead of finalamount
      const refundAmount = parseFloat(selectedRefund.refund_amount || 0);

      // Call your API with refundid as a query parameter
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/initiate_refund?refundid=${encodeURIComponent(selectedRefund.refundid)}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // Handle error response
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Refund initiation failed');
      }

      // Handle different response scenarios
      if (data.message === 'No refund required' || refundAmount <= 0) {
        toast.info('No refund required for this transaction');

        // Update refund status in local state
        updateRefundStatus(selectedRefund.refundid, 'No Refund Required');

        setShowConfirmModal(false);
        setProcessing(false);
        fetchRefunds(); // Refresh the list
        return;
      }

      // Success - refund initiated with Razorpay
      toast.success('Refund initiated successfully!');

      // Update the refund in local state with Razorpay details
      updateRefundStatus(
        selectedRefund.refundid,
        data.status || 'initiated',
        data.razorpay_refund_id
      );

      setShowConfirmModal(false);
      setProcessing(false);

      // Show detailed success message with refund amount
      toast.info(
        <div className="text-sm">
          <p className="font-semibold text-green-600">✅ Refund Initiated</p>
          <p>Refund Amount: ₹{refundAmount.toFixed(2)}</p>
          {data.razorpay_refund_id && (
            <>
              <p>
                Razorpay Refund ID:{' '}
                <span className="font-mono">{maskId(data.razorpay_refund_id)}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">Full ID: {data.razorpay_refund_id}</p>
            </>
          )}
          <p className="text-xs text-blue-600 mt-2">
            ⏱️ Refund will be credited within 3-5 business days
          </p>
        </div>,
        { autoClose: 8000 }
      );
    } catch (error) {
      console.error('Error processing refund:', error);

      // Handle specific error messages
      if (error.message.includes('already')) {
        toast.error('Refund already processed for this transaction');
        fetchRefunds(); // Refresh data
      } else if (error.message.includes('not found')) {
        toast.error(error.message || 'Refund or payment transaction not found');
      } else {
        toast.error(error.message || 'Failed to process refund');
      }

      setProcessing(false);
    }
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(searchValue);
    filterData(searchValue, activeStatus, timeRange, selectedRefundStatus);
  };

  const handleActiveStatusChange = (e) => {
    const status = e.target.value;
    setActiveStatus(status);
    filterData(searchTerm, status, timeRange, selectedRefundStatus);
  };

  const handleTimeRangeChange = (e) => {
    const range = e.target.value;
    setTimeRange(range);
    filterData(searchTerm, activeStatus, range, selectedRefundStatus);
  };

  const handleRefundStatusChange = (e) => {
    const status = e.target.value;
    setSelectedRefundStatus(status);
    filterData(searchTerm, activeStatus, timeRange, status);
  };

  const filterData = (searchValue, status, timeRange, refundStatus) => {
    let filtered = allRefunds.filter(
      (item) =>
        (item.refundid && item.refundid.toLowerCase().includes(searchValue)) ||
        (item.booking_info && getGuestName(item).toLowerCase().includes(searchValue)) ||
        (item.booking_info &&
          item.booking_info.orderid &&
          item.booking_info.orderid.toLowerCase().includes(searchValue))
    );

    if (refundStatus !== 'all') {
      filtered = filtered.filter((item) => item.refund_status === refundStatus);
    }

    if (timeRange !== 'all') {
      const currentDate = new Date();
      const startDate = new Date();

      if (timeRange === 'day') {
        startDate.setDate(currentDate.getDate() - 1);
      } else if (timeRange === 'week') {
        startDate.setDate(currentDate.getDate() - 7);
      } else if (timeRange === 'month') {
        startDate.setMonth(currentDate.getMonth() - 1);
      }

      filtered = filtered.filter((item) => {
        if (!item.createdate) return false;
        const itemDate = new Date(item.createdate);
        return itemDate >= startDate;
      });
    }

    setFilteredRefunds(filtered);
    setCurrentPage(1);
  };

  const handleSearchRefunds = async () => {
    if (!searchTerm.trim()) {
      toast.info('Please enter search criteria');
      return;
    }

    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/admin/refunds/search?query=${encodeURIComponent(searchTerm)}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (data.success) {
        setFilteredRefunds(data.refunds);
        setCurrentPage(1);
        toast.success(`Found ${data.total_results} results`);
      }
    } catch (error) {
      console.error('Error searching refunds:', error);
      toast.error('Failed to search refunds');
    }
  };

  const handleUpdateRefund = async () => {
    if (!selectedRefund) return;

    try {
      const payload = {
        refund_amount: updatedRefundData.refund_amount
          ? parseFloat(updatedRefundData.refund_amount)
          : null,
        service_fee: updatedRefundData.service_fee
          ? parseFloat(updatedRefundData.service_fee)
          : null,
        refund_reason: updatedRefundData.refund_reason || null,
        admin_notes: updatedRefundData.admin_notes || null,
      };

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/admin/refunds/${selectedRefund.refundid}`,
        {
          method: 'PUT',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.detail || data.message || 'Refund update failed';
        throw new Error(errorMessage);
      }

      toast.success('Refund updated successfully!');
      setShowUpdateModal(false);
      fetchRefunds(); // Refresh the list
    } catch (error) {
      console.error('Error updating refund:', error);
      toast.error(error.message || 'Failed to update refund');
    }
  };

  const handleViewDetails = (refund) => {
    setSelectedRefund(refund);
    fetchRefundDetails(refund.refundid);
    setShowDetailsModal(true);
  };

  const handleEditRefund = (refund) => {
    setSelectedRefund(refund);
    setUpdatedRefundData({
      refund_amount: refund.refund_amount || '',
      service_fee: refund.service_fee || '',
      refund_reason: refund.refund_reason || '',
      admin_notes: '',
    });
    setShowUpdateModal(true);
  };

  const handleRazorpayRefundClick = async (refund) => {
    setSelectedRefund(refund);
    setUpdatedRefundData({
      admin_notes: '',
    });

    // Fetch detailed refund information to get phone number
    setLoadingDetails(true);
    setShowConfirmModal(true);

    try {
      const details = await fetchRefundDetails(refund.refundid);
      if (details) {
        // Create an enriched refund object with details
        const enrichedRefund = {
          ...refund,
          booking_details: details.booking_details,
          payment_details: details.payment_details,
        };
        setSelectedRefundDetails(enrichedRefund);
      } else {
        setSelectedRefundDetails(refund);
      }
    } catch (error) {
      console.error('Error fetching refund details:', error);
      setSelectedRefundDetails(refund);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Check if refund can be processed via Razorpay - using refund_amount
  const canProcessRazorpayRefund = (refund) => {
    if (!refund || !refund.refund_status) return false;

    const status = refund.refund_status.toLowerCase();
    const refundAmount = parseFloat(refund.refund_amount || 0);

    // Can process if: status is pending/created, refund amount > 0, not already processed
    const isPending =
      status.includes('pending') || status.includes('created') || status === null || status === '';
    const hasValidAmount = refundAmount > 0;
    const isNotProcessed =
      !status.includes('processed') &&
      !status.includes('success') &&
      !status.includes('failed') &&
      !status.includes('initiated') &&
      !status.includes('no refund');

    return isPending && hasValidAmount && isNotProcessed;
  };

  const getStatusBadge = (status) => {
    const lowerStatus = (status || '').toLowerCase();

    if (lowerStatus.includes('pending') || lowerStatus.includes('initiated')) {
      return 'bg-yellow-100 text-yellow-800';
    } else if (lowerStatus.includes('success') || lowerStatus.includes('processed')) {
      return 'bg-green-100 text-green-800';
    } else if (lowerStatus.includes('failed') || lowerStatus.includes('rejected')) {
      return 'bg-red-100 text-red-800';
    } else if (lowerStatus.includes('no refund')) {
      return 'bg-gray-100 text-gray-800';
    } else {
      return 'bg-blue-100 text-blue-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalPages = Math.ceil(filteredRefunds.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredRefunds.slice(indexOfFirstRow, indexOfLastRow);

  const handlePageChange = (page) => setCurrentPage(page);
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const getVisiblePages = () => {
    let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let end = Math.min(totalPages, start + maxVisiblePages - 1);
    if (end - start < maxVisiblePages - 1) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen flex flex-col">
      <ToastContainer />
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">P M S</h1>
        <div className="flex space-x-6">
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-operations')
            }
            className="btn "
          >
            Front Desk Operations
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/guest-managment')
            }
            className="btn "
          >
            Guest Management
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-roomservices')
            }
            className="btn"
          >
            Hospitality Services
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-amenities')
            }
            className="btn "
          >
            Facility Services
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/billing-payments')
            }
            className="btn"
          >
            Billing & Payments
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/refunds-adjustments')
            }
            className="btn  text-black font-bold"
          >
            Refunds & Adjustments
          </button>
        </div>
      </header>

      <div className="text-center mb-6">
        <div className="inline-block">
          <h1 className="text-2xl font-bold text-center text-black">Refunds & Adjustments</h1>
          <h1 className="text-sm font-poppins font-semibold text-center text-gray-600">
            Manage refunds and adjustments with detailed tracking and reporting
          </h1>
          <hr className="border-gray-400 mt-1 w-60 mx-auto mb-4" />
        </div>
      </div>

      {/* Statistics Section */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Refunds</h3>
            <p className="text-2xl font-bold text-black">{statistics.total_refunds}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Refund Amount</h3>
            <p className="text-2xl font-bold text-green-600">
              ₹{statistics.total_refund_amount?.toFixed(2) || '0.00'}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Average Refund</h3>
            <p className="text-2xl font-bold text-blue-600">
              ₹{statistics.average_refund_amount?.toFixed(2) || '0.00'}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Service Fee</h3>
            <p className="text-2xl font-bold text-yellow-500">
              ₹{statistics.total_service_fee?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-col md:flex-row items-center gap-4">
        <div className="relative w-full md:w-1/3">
          <input
            type="text"
            placeholder="Search by refund ID, guest name, or order number"
            value={searchTerm}
            onChange={handleSearch}
            className="p-3 pl-12 h-12 border w-full border-gray-300 rounded-lg shadow-md"
          />
          <FiSearch className="absolute top-3 left-4 text-gray-500" size={20} />
        </div>

        <div className="flex flex-wrap gap-2 w-full md:w-auto justify-end">
          <button
            onClick={handleSearchRefunds}
            className="px-4 py-3 h-12 bg-black text-white rounded-lg shadow-md hover:bg-gray-800 transition"
          >
            Search
          </button>

          <select
            onChange={handleTimeRangeChange}
            value={timeRange}
            className="p-3 h-12 border rounded-lg shadow-md"
          >
            <option value="all">All Time</option>
            <option value="day">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>

          <select
            onChange={handleRefundStatusChange}
            value={selectedRefundStatus}
            className="p-3 h-12 border rounded-lg shadow-md"
          >
            <option value="all">All Status</option>
            {refundStatuses.map((status, index) => (
              <option key={index} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto bg-white shadow-md rounded-md">
        <table className="min-w-full bg-white border border-gray-300">
          <thead>
            <tr className="bg-gray-200 text-black font-semibold text-base">
              <th className="py-3 px-2 border border-gray-300">Sr No.</th>
              <th className="py-3 px-4 border border-gray-300">Refund ID</th>
              <th className="py-3 px-4 border border-gray-300">Guest</th>
              <th className="py-3 px-4 border border-gray-300">Order ID</th>
              <th className="py-3 px-4 border border-gray-300">Refund Amount</th>
              <th className="py-3 px-4 border border-gray-300">Final Amount</th>
              <th className="py-3 px-4 border border-gray-300">Status</th>
              <th className="py-3 px-4 border border-gray-300">Razorpay Refund ID</th>
              <th className="py-3 px-4 border border-gray-300">Created Date</th>
              <th className="py-3 px-2 border border-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              currentRows.map((refund, index) => (
                <tr key={refund.refundid} className="hover:bg-gray-50 border">
                  <td className="py-2 px-4 border text-center">{indexOfFirstRow + index + 1}</td>
                  <td className="py-2 px-4 border text-sm font-medium">
                    <span title={refund.refundid}>{maskId(refund.refundid)}</span>
                  </td>
                  <td className="py-2 px-4 border text-sm">{getGuestName(refund)}</td>
                  <td className="py-2 px-4 border font-medium">
                    {refund.booking_info?.orderid || 'N/A'}
                  </td>
                  <td className="py-2 px-4 border font-medium">
                    <span className="text-green-600 font-bold">
                      ₹{parseFloat(refund.refund_amount || 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="py-2 px-4 border font-medium">
                    ₹{parseFloat(refund.finalamount || 0).toFixed(2)}
                  </td>
                  <td className="py-2 px-4 border">
                    <span
                      className={`px-3 py-1 rounded-full text-xs ${getStatusBadge(refund.refund_status)}`}
                    >
                      {refund.refund_status || 'Pending'}
                    </span>
                  </td>
                  <td className="py-2 px-4 border">
                    {refund.razorpay_refund_id ? (
                      <span className="text-xs text-gray-600" title={refund.razorpay_refund_id}>
                        {maskId(refund.razorpay_refund_id)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Not initiated</span>
                    )}
                  </td>
                  <td className="py-2 px-4 border text-sm">{formatDate(refund.createdate)}</td>
                  <td className="py-2 px-1 border">
                    <div className="flex gap-2 justify-center">
                      <button
                        className="px-3 py-1 bg-black text-white rounded hover:bg-gray-700 transition text-sm flex items-center gap-1"
                        onClick={() => handleViewDetails(refund)}
                      >
                        <FiSearch size={14} /> View
                      </button>

                      {(!refund.refund_status ||
                        refund.refund_status === 'Pending' ||
                        refund.refund_status === 'created' ||
                        refund.refund_status === 'No Refund Required') && (
                        <>
                          <button
                            className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition text-sm flex items-center gap-1"
                            onClick={() => handleEditRefund(refund)}
                          >
                            <FiEdit size={14} /> Edit
                          </button>

                          {canProcessRazorpayRefund(refund) ? (
                            <button
                              className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition text-sm flex items-center gap-1"
                              onClick={() => handleRazorpayRefundClick(refund)}
                            >
                              <FiCreditCard size={14} /> Refund
                            </button>
                          ) : (
                            <button
                              className="px-3 py-1 bg-gray-300 text-gray-600 rounded cursor-not-allowed text-sm flex items-center gap-1"
                              disabled
                              title={
                                refund.refund_status === 'No Refund Required'
                                  ? 'No refund required'
                                  : 'Cannot process refund'
                              }
                            >
                              <FiX size={14} /> Refund
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="text-center py-8">
                  <div className="text-gray-500">
                    <p className="mb-2">No refund records found</p>
                    <p className="text-sm">Try adjusting your search criteria</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center mt-8 text-xs gap-2">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded ${
            currentPage === 1
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-black text-white hover:bg-gray-800'
          }`}
        >
          Previous
        </button>
        {getVisiblePages().map((page) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`px-3 py-2 mx-1 rounded-full ${
              currentPage === page
                ? 'bg-black text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {page}
          </button>
        ))}
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 rounded ${
            currentPage === totalPages
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-black text-white hover:bg-gray-800'
          }`}
        >
          Next
        </button>
      </div>

      {/* Refund Details Modal */}
      {showDetailsModal && refundDetails && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50 overflow-y-auto">
          <div className="bg-white p-6 rounded-lg shadow-xl w-[90%] max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl text-black font-bold">Refund Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Refund Information */}
              <div className="space-y-4">
                <h3 className="font-bold text-lg border-b pb-2">Refund Information</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <p className="text-gray-700 font-medium">Refund ID:</p>
                    <p className="text-gray-700 font-mono" title={refundDetails.refundid}>
                      {maskId(refundDetails.refundid)}
                    </p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-gray-700 font-medium">Billing ID:</p>
                    <p className="text-gray-700" title={refundDetails.billingid}>
                      {maskId(refundDetails.billingid)}
                    </p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-gray-700 font-medium">Status:</p>
                    <span
                      className={`px-3 py-1 rounded-full text-xs ${getStatusBadge(refundDetails.refund_status)}`}
                    >
                      {refundDetails.refund_status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-gray-700 font-medium">Razorpay Refund ID:</p>
                    <p className="text-gray-700" title={refundDetails.razorpay_refund_id}>
                      {refundDetails.razorpay_refund_id
                        ? maskId(refundDetails.razorpay_refund_id)
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-gray-700 font-medium">Refund Reason:</p>
                    <p className="text-gray-700 text-right max-w-xs">
                      {refundDetails.refund_reason || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Booking Information */}
              {refundDetails.booking_details && (
                <div className="space-y-4">
                  <h3 className="font-bold text-lg border-b pb-2">Reservation Information</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <p className="text-gray-700 font-medium">Reservation Number:</p>
                      <p className="text-gray-700">{refundDetails.booking_details.orderid}</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-gray-700 font-medium">Guest:</p>
                      <p className="text-gray-700">
                        {refundDetails.booking_details.guest_details
                          ? `${refundDetails.booking_details.guest_details.firstname || ''} ${refundDetails.booking_details.guest_details.lastname || ''}`.trim()
                          : 'N/A'}
                      </p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-gray-700 font-medium">Phone:</p>
                      <p className="text-gray-700">
                        {refundDetails.booking_details.guest_details?.phonenumber || 'N/A'}
                      </p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-gray-700 font-medium">Room Type:</p>
                      <p className="text-gray-700">
                        {refundDetails.booking_details.room_details?.roomtypename || 'N/A'}
                      </p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-gray-700 font-medium">Check-in:</p>
                      <p className="text-gray-700">
                        {formatDate(refundDetails.booking_details.checkin_date)}
                      </p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-gray-700 font-medium">Check-out:</p>
                      <p className="text-gray-700">
                        {formatDate(refundDetails.booking_details.checkout_date)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Amount Details */}
              <div className="space-y-4">
                <h3 className="font-bold text-lg border-b pb-2">Amount Details</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <p className="text-gray-700 font-medium">Refund Amount:</p>
                    <p className=" font-bold text-green-600">
                      ₹{parseFloat(refundDetails.refund_amount || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-gray-700 font-medium">Service Fee:</p>
                    <p className="text-gray-700">
                      ₹{parseFloat(refundDetails.service_fee || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <p className="text-gray-700 font-medium">Final Amount:</p>
                    <p className="text-gray-700 font-bold text-lg">
                      ₹{parseFloat(refundDetails.finalamount || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Amenities Summary */}
              {refundDetails.amenities_summary && (
                <div className="space-y-4">
                  <h3 className="font-bold text-lg border-b pb-2">Amenities Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <p className="text-gray-700 font-medium">Refundable Amenities:</p>
                      <p className="text-green-600">
                        ₹
                        {parseFloat(
                          refundDetails.amenities_summary.refundable_amenities_total || 0
                        ).toFixed(2)}
                      </p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-gray-700 font-medium">Non-refundable Amenities:</p>
                      <p className="text-red-600">
                        ₹
                        {parseFloat(
                          refundDetails.amenities_summary.non_refundable_amenities_total || 0
                        ).toFixed(2)}
                      </p>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <p className="text-gray-700 font-medium">Total Amenities:</p>
                      <p className="text-gray-700 font-bold">
                        ₹
                        {parseFloat(
                          refundDetails.amenities_summary.total_amenities_amount || 0
                        ).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Details */}
            {refundDetails.payment_details && refundDetails.payment_details.length > 0 && (
              <div className="mt-6">
                <h3 className="font-bold text-lg border-b pb-2 mb-4">Payment Details</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-300">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="py-2 px-4 border">Transaction ID</th>
                        <th className="py-2 px-4 border">Amount</th>
                        <th className="py-2 px-4 border">Status</th>
                        <th className="py-2 px-4 border">Method</th>
                        <th className="py-2 px-4 border">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {refundDetails.payment_details.map((payment, idx) => (
                        <tr key={idx}>
                          <td className="py-2 px-4 border text-sm" title={payment.transactionid}>
                            {maskId(payment.transactionid)}
                          </td>
                          <td className="py-2 px-4 border">
                            ₹{parseFloat(payment.finalamount || 0).toFixed(2)}
                          </td>
                          <td className="py-2 px-4 border">
                            <span
                              className={`px-2 py-1 rounded text-xs ${payment.paymentstatus === 'Success' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
                            >
                              {payment.paymentstatus}
                            </span>
                          </td>
                          <td className="py-2 px-4 border">{payment.paymentmethod}</td>
                          <td className="py-2 px-4 border text-sm">
                            {formatDateTime(payment.created_date)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Refund Modal */}
      {showUpdateModal && selectedRefund && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-[470px]">
            <h2 className="text-xl text-black font-bold mb-4">Update Refund Details</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Refund Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={updatedRefundData.refund_amount}
                  onChange={(e) =>
                    setUpdatedRefundData({ ...updatedRefundData, refund_amount: e.target.value })
                  }
                  className="w-full p-2 border rounded"
                  placeholder="Enter refund amount"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Service Fee</label>
                <input
                  type="number"
                  step="0.01"
                  value={updatedRefundData.service_fee}
                  onChange={(e) =>
                    setUpdatedRefundData({ ...updatedRefundData, service_fee: e.target.value })
                  }
                  className="w-full p-2 border rounded"
                  placeholder="Enter service fee"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Refund Reason</label>
                <textarea
                  value={updatedRefundData.refund_reason}
                  onChange={(e) =>
                    setUpdatedRefundData({ ...updatedRefundData, refund_reason: e.target.value })
                  }
                  className="w-full p-2 border rounded"
                  rows="3"
                  placeholder="Enter refund reason"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Admin Notes</label>
                <textarea
                  value={updatedRefundData.admin_notes}
                  onChange={(e) =>
                    setUpdatedRefundData({ ...updatedRefundData, admin_notes: e.target.value })
                  }
                  className="w-full p-2 border rounded"
                  rows="2"
                  placeholder="Additional notes (optional)"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:text-white transition"
                onClick={() => setShowUpdateModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:text-white transition"
                onClick={handleUpdateRefund}
              >
                Update Refund
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Razorpay Refund Confirmation Modal */}
      {showConfirmModal && selectedRefund && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-[500px]">
            <div className="flex items-center mb-4">
              <FiCreditCard className="text-green-500 mr-3" size={24} />
              <h2 className="text-xl text-black font-bold">Confirm Refund</h2>
              {loadingDetails && (
                <div className="ml-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-green-500"></div>
                </div>
              )}
            </div>

            <div className="space-y-4 mb-6 bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-700 font-medium text-sm">Refund ID:</p>
                  <p className="text-gray-900 font-mono text-sm" title={selectedRefund.refundid}>
                    {maskId(selectedRefund.refundid)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-700 font-medium text-sm">Reservation Number:</p>
                  <p className="text-gray-900 font-medium">
                    {selectedRefund.booking_info?.orderid || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-700 font-medium text-sm">Guest:</p>
                  <p className="text-gray-900">
                    {selectedRefundDetails
                      ? getGuestName(selectedRefundDetails)
                      : getGuestName(selectedRefund)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-700 font-medium text-sm">Phone:</p>
                  <p className="text-gray-900 font-medium">
                    {loadingDetails ? (
                      <span className="text-gray-400">Loading...</span>
                    ) : selectedRefundDetails ? (
                      getGuestPhone(selectedRefundDetails)
                    ) : (
                      getGuestPhone(selectedRefund)
                    )}
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-gray-700 font-medium">Refund Amount:</p>
                    <p className="text-gray-500 text-xs">
                      Amount to be refunded from refund_amount
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    ₹{parseFloat(selectedRefund.refund_amount || 0).toFixed(2)}
                  </p>
                </div>

                {selectedRefund.service_fee > 0 && (
                  <div className="flex justify-between mt-2 text-sm">
                    <p className="text-gray-600">Service Fee (not refundable):</p>
                    <p className="text-red-600">
                      -₹{parseFloat(selectedRefund.service_fee || 0).toFixed(2)}
                    </p>
                  </div>
                )}

                <div className="flex justify-between mt-3 pt-3 border-t text-sm">
                  <p className="text-gray-700 font-medium">Final Amount (for reference):</p>
                  <p className="font-bold text-black">
                    ₹{parseFloat(selectedRefund.finalamount || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Payment Method Info */}
              {(selectedRefundDetails?.payment_details?.length > 0 ||
                selectedRefund.payment_details?.length > 0) && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-800 font-medium">Original Payment Method:</p>
                  <p className="text-sm mt-1">
                    {selectedRefundDetails?.payment_details?.[0]?.paymentmethod ||
                      selectedRefund.payment_details?.[0]?.paymentmethod ||
                      'Unknown'}
                    {(selectedRefundDetails?.payment_details?.[0]?.paymentgateway ||
                      selectedRefund.payment_details?.[0]?.paymentgateway) &&
                      ` via ${
                        selectedRefundDetails?.payment_details?.[0]?.paymentgateway ||
                        selectedRefund.payment_details?.[0]?.paymentgateway
                      }`}
                  </p>
                </div>
              )}
            </div>

            {/* Admin Notes (Optional) */}
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Admin Notes (Optional)
              </label>
              <textarea
                value={updatedRefundData.admin_notes || ''}
                onChange={(e) =>
                  setUpdatedRefundData({ ...updatedRefundData, admin_notes: e.target.value })
                }
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="2"
                placeholder="Add any internal notes about this refund..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Notes are for internal use only and won't be shared with the guest
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition font-medium"
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedRefundDetails(null);
                }}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleInitiateRefund}
                disabled={processing || loadingDetails}
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <FiCreditCard size={16} />
                    Process Refund (₹{parseFloat(selectedRefund.refund_amount || 0).toFixed(2)})
                  </>
                )}
              </button>
            </div>

            {/* Refund Timing Info */}
            <div className="mt-4 text-xs text-gray-500 border-t pt-3">
              <p>• Refund will be processed to the original payment method</p>
              <p>• Guest will receive confirmation from Razorpay via SMS/email</p>
              <p>
                • Bank transfers may take 3-5 business days, UPI/wallet refunds are usually instant
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundAdjustments;

// import React, { useState, useEffect } from "react";
// import { FiSearch, FiEdit, FiTrash2, FiCheck, FiX, FiCreditCard } from "react-icons/fi";
// import { useNavigate } from "react-router-dom";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const RefundAdjustments = () => {
//   const [allRefunds, setAllRefunds] = useState([]);
//   const [filteredRefunds, setFilteredRefunds] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);
//   const [activeStatus, setActiveStatus] = useState("all");
//   const [timeRange, setTimeRange] = useState("all");
//   const [refundStatuses, setRefundStatuses] = useState([]);
//   const [selectedRefundStatus, setSelectedRefundStatus] = useState("all");
//   const [statistics, setStatistics] = useState(null);
//   const [showRefundModal, setShowRefundModal] = useState(false);
//   const [showUpdateModal, setShowUpdateModal] = useState(false);
//   const [showDetailsModal, setShowDetailsModal] = useState(false);
//   const [showConfirmModal, setShowConfirmModal] = useState(false);
//   const [selectedRefund, setSelectedRefund] = useState(null);
//   const [updatedRefundData, setUpdatedRefundData] = useState({
//     refund_amount: "",
//     service_fee: "",
//     refund_reason: "",
//     admin_notes: ""
//   });
//   const [refundDetails, setRefundDetails] = useState(null);
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);
//   const [processing, setProcessing] = useState(false);

//   const rowsPerPage = 15;
//   const maxVisiblePages = 10;
//   const navigate = useNavigate();

//   useEffect(() => {
//     // Load Razorpay script (optional, not strictly needed for refunds but kept for consistency)
//     const loadRazorpayScript = () => {
//       const script = document.createElement("script");
//       script.src = "https://checkout.razorpay.com/v1/checkout.js";
//       script.async = true;
//       script.onload = () => setRazorpayLoaded(true);
//       script.onerror = () => {
//         console.error("Failed to load Razorpay script");
//         toast.error("Error loading payment system. Please refresh the page.");
//       };
//       document.body.appendChild(script);
//     };

//     if (!razorpayLoaded) {
//       loadRazorpayScript();
//     }

//     fetchRefunds();
//     fetchStatistics();
//   }, []);

//   // Helper function to mask IDs
//   const maskId = (id) => {
//     if (!id) return "N/A";
//     if (id.length <= 8) return id;
//     return `${id.substring(0, 6)}...${id.substring(id.length - 4)}`;
//   };

//   const fetchRefunds = async () => {
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/admin/refunds`,
//         {
//           method: "GET",
//           headers: {
//             accept: "application/json",
//           },
//         }
//       );

//       if (!response.ok) {
//         throw new Error("Network response was not ok");
//       }

//       const data = await response.json();

//       if (data.success && data.refunds) {
//         setAllRefunds(data.refunds);
//         setFilteredRefunds(data.refunds);

//         // Get unique refund statuses
//         const uniqueStatuses = [
//           ...new Set(
//             data.refunds.map((item) => item.refund_status).filter(Boolean)
//           ),
//         ].sort();

//         setRefundStatuses(uniqueStatuses);
//       }
//     } catch (error) {
//       console.error("Error fetching refunds:", error);
//       toast.error("Failed to fetch refund data");
//     }
//   };

//   const fetchStatistics = async () => {
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/admin/refunds/statistics`,
//         {
//           method: "GET",
//           headers: {
//             accept: "application/json",
//           },
//         }
//       );

//       if (!response.ok) {
//         throw new Error("Network response was not ok");
//       }

//       const data = await response.json();

//       if (data.success) {
//         setStatistics(data.statistics);
//       }
//     } catch (error) {
//       console.error("Error fetching statistics:", error);
//     }
//   };

//   const fetchRefundDetails = async (refundid) => {
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/admin/refunds/${refundid}`,
//         {
//           method: "GET",
//           headers: {
//             accept: "application/json",
//           },
//         }
//       );

//       if (!response.ok) {
//         throw new Error("Network response was not ok");
//       }

//       const data = await response.json();
//       setRefundDetails(data);
//     } catch (error) {
//       console.error("Error fetching refund details:", error);
//       toast.error("Failed to fetch refund details");
//     }
//   };

//   // Helper function to update refund status in local state
//   const updateRefundStatus = (refundId, status, razorpayRefundId = null) => {
//     setAllRefunds(prev =>
//       prev.map(item =>
//         item.refundid === refundId
//           ? {
//               ...item,
//               refund_status: status,
//               razorpay_refund_id: razorpayRefundId || item.razorpay_refund_id,
//               updatedate: new Date().toISOString()
//             }
//           : item
//       )
//     );

//     setFilteredRefunds(prev =>
//       prev.map(item =>
//         item.refundid === refundId
//           ? {
//               ...item,
//               refund_status: status,
//               razorpay_refund_id: razorpayRefundId || item.razorpay_refund_id,
//               updatedate: new Date().toISOString()
//             }
//           : item
//       )
//     );
//   };

//   const handleInitiateRefund = async () => {
//     if (!selectedRefund) return;

//     setProcessing(true);

//     try {
//       // Use refund_amount instead of finalamount
//       const refundAmount = parseFloat(selectedRefund.refund_amount || 0);

//       // Call your API with refundid as a query parameter
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/initiate_refund?refundid=${encodeURIComponent(selectedRefund.refundid)}`,
//         {
//           method: "POST",
//           headers: {
//             accept: "application/json",
//           },
//         }
//       );

//       const data = await response.json();

//       if (!response.ok) {
//         // Handle error response
//         const errorData = await response.json().catch(() => ({}));
//         throw new Error(errorData.detail || errorData.message || "Refund initiation failed");
//       }

//       // Handle different response scenarios
//       if (data.message === "No refund required" || refundAmount <= 0) {
//         toast.info("No refund required for this transaction");

//         // Update refund status in local state
//         updateRefundStatus(selectedRefund.refundid, "No Refund Required");

//         setShowConfirmModal(false);
//         setProcessing(false);
//         fetchRefunds(); // Refresh the list
//         return;
//       }

//       // Success - refund initiated with Razorpay
//       toast.success("Refund initiated successfully!");

//       // Update the refund in local state with Razorpay details
//       updateRefundStatus(
//         selectedRefund.refundid,
//         data.status || "initiated",
//         data.razorpay_refund_id
//       );

//       setShowConfirmModal(false);
//       setProcessing(false);

//       // Show detailed success message with refund amount
//       toast.info(
//         <div className="text-sm">
//           <p className="font-semibold text-green-600">✅ Refund Initiated</p>
//           <p>Refund Amount: ₹{refundAmount.toFixed(2)}</p>
//           {data.razorpay_refund_id && (
//             <>
//               <p>Razorpay Refund ID: <span className="font-mono">{maskId(data.razorpay_refund_id)}</span></p>
//               <p className="text-xs text-gray-500 mt-1">Full ID: {data.razorpay_refund_id}</p>
//             </>
//           )}
//           <p className="text-xs text-blue-600 mt-2">⏱️ Refund will be credited within 3-5 business days</p>
//         </div>,
//         { autoClose: 8000 }
//       );

//     } catch (error) {
//       console.error("Error processing refund:", error);

//       // Handle specific error messages
//       if (error.message.includes("already")) {
//         toast.error("Refund already processed for this transaction");
//         fetchRefunds(); // Refresh data
//       } else if (error.message.includes("not found")) {
//         toast.error(error.message || "Refund or payment transaction not found");
//       } else {
//         toast.error(error.message || "Failed to process refund");
//       }

//       setProcessing(false);
//     }
//   };

//   const handleSearch = (e) => {
//     const searchValue = e.target.value.toLowerCase();
//     setSearchTerm(searchValue);
//     filterData(searchValue, activeStatus, timeRange, selectedRefundStatus);
//   };

//   const handleActiveStatusChange = (e) => {
//     const status = e.target.value;
//     setActiveStatus(status);
//     filterData(searchTerm, status, timeRange, selectedRefundStatus);
//   };

//   const handleTimeRangeChange = (e) => {
//     const range = e.target.value;
//     setTimeRange(range);
//     filterData(searchTerm, activeStatus, range, selectedRefundStatus);
//   };

//   const handleRefundStatusChange = (e) => {
//     const status = e.target.value;
//     setSelectedRefundStatus(status);
//     filterData(searchTerm, activeStatus, timeRange, status);
//   };

//   const filterData = (searchValue, status, timeRange, refundStatus) => {
//     let filtered = allRefunds.filter(
//       (item) =>
//         (item.refundid && item.refundid.toLowerCase().includes(searchValue)) ||
//         (item.booking_info && item.booking_info.guest_name &&
//           item.booking_info.guest_name.toLowerCase().includes(searchValue)) ||
//         (item.booking_info && item.booking_info.orderid &&
//           item.booking_info.orderid.toLowerCase().includes(searchValue))
//     );

//     if (refundStatus !== "all") {
//       filtered = filtered.filter((item) => item.refund_status === refundStatus);
//     }

//     if (timeRange !== "all") {
//       const currentDate = new Date();
//       const startDate = new Date();

//       if (timeRange === "day") {
//         startDate.setDate(currentDate.getDate() - 1);
//       } else if (timeRange === "week") {
//         startDate.setDate(currentDate.getDate() - 7);
//       } else if (timeRange === "month") {
//         startDate.setMonth(currentDate.getMonth() - 1);
//       }

//       filtered = filtered.filter((item) => {
//         if (!item.createdate) return false;
//         const itemDate = new Date(item.createdate);
//         return itemDate >= startDate;
//       });
//     }

//     setFilteredRefunds(filtered);
//     setCurrentPage(1);
//   };

//   const handleSearchRefunds = async () => {
//     if (!searchTerm.trim()) {
//       toast.info("Please enter search criteria");
//       return;
//     }

//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/admin/refunds/search?query=${encodeURIComponent(searchTerm)}`,
//         {
//           method: "GET",
//           headers: {
//             accept: "application/json",
//           },
//         }
//       );

//       if (!response.ok) {
//         throw new Error("Network response was not ok");
//       }

//       const data = await response.json();

//       if (data.success) {
//         setFilteredRefunds(data.refunds);
//         setCurrentPage(1);
//         toast.success(`Found ${data.total_results} results`);
//       }
//     } catch (error) {
//       console.error("Error searching refunds:", error);
//       toast.error("Failed to search refunds");
//     }
//   };

//   const handleUpdateRefund = async () => {
//     if (!selectedRefund) return;

//     try {
//       const payload = {
//         refund_amount: updatedRefundData.refund_amount ? parseFloat(updatedRefundData.refund_amount) : null,
//         service_fee: updatedRefundData.service_fee ? parseFloat(updatedRefundData.service_fee) : null,
//         refund_reason: updatedRefundData.refund_reason || null,
//         admin_notes: updatedRefundData.admin_notes || null
//       };

//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/admin/refunds/${selectedRefund.refundid}`,
//         {
//           method: "PUT",
//           headers: {
//             accept: "application/json",
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(payload),
//         }
//       );

//       const data = await response.json();

//       if (!response.ok) {
//         const errorMessage = data.detail || data.message || "Refund update failed";
//         throw new Error(errorMessage);
//       }

//       toast.success("Refund updated successfully!");
//       setShowUpdateModal(false);
//       fetchRefunds(); // Refresh the list
//     } catch (error) {
//       console.error("Error updating refund:", error);
//       toast.error(error.message || "Failed to update refund");
//     }
//   };

//   const handleViewDetails = (refund) => {
//     setSelectedRefund(refund);
//     fetchRefundDetails(refund.refundid);
//     setShowDetailsModal(true);
//   };

//   const handleEditRefund = (refund) => {
//     setSelectedRefund(refund);
//     setUpdatedRefundData({
//       refund_amount: refund.refund_amount || "",
//       service_fee: refund.service_fee || "",
//       refund_reason: refund.refund_reason || "",
//       admin_notes: ""
//     });
//     setShowUpdateModal(true);
//   };

//   const handleRazorpayRefundClick = (refund) => {
//     setSelectedRefund(refund);
//     setUpdatedRefundData({
//       admin_notes: ""
//     });
//     setShowConfirmModal(true);
//   };

//   // Check if refund can be processed via Razorpay - using refund_amount
//   const canProcessRazorpayRefund = (refund) => {
//     if (!refund || !refund.refund_status) return false;

//     const status = refund.refund_status.toLowerCase();
//     const refundAmount = parseFloat(refund.refund_amount || 0);

//     // Can process if: status is pending/created, refund amount > 0, not already processed
//     const isPending = status.includes("pending") || status.includes("created") ||
//                      status === null || status === "";
//     const hasValidAmount = refundAmount > 0;
//     const isNotProcessed = !status.includes("processed") &&
//                           !status.includes("success") &&
//                           !status.includes("failed") &&
//                           !status.includes("initiated") &&
//                           !status.includes("no refund");

//     return isPending && hasValidAmount && isNotProcessed;
//   };

//   const getStatusBadge = (status) => {
//     const lowerStatus = (status || "").toLowerCase();

//     if (lowerStatus.includes("pending") || lowerStatus.includes("initiated")) {
//       return "bg-yellow-100 text-yellow-800";
//     } else if (lowerStatus.includes("success") || lowerStatus.includes("processed")) {
//       return "bg-green-100 text-green-800";
//     } else if (lowerStatus.includes("failed") || lowerStatus.includes("rejected")) {
//       return "bg-red-100 text-red-800";
//     } else if (lowerStatus.includes("no refund")) {
//       return "bg-gray-100 text-gray-800";
//     } else {
//       return "bg-blue-100 text-blue-800";
//     }
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return "N/A";
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-IN', {
//       day: '2-digit',
//       month: 'short',
//       year: 'numeric'
//     });
//   };

//   const formatDateTime = (dateString) => {
//     if (!dateString) return "N/A";
//     const date = new Date(dateString);
//     return date.toLocaleString('en-IN', {
//       day: '2-digit',
//       month: 'short',
//       year: 'numeric',
//       hour: '2-digit',
//       minute: '2-digit'
//     });
//   };

//   const totalPages = Math.ceil(filteredRefunds.length / rowsPerPage);
//   const indexOfLastRow = currentPage * rowsPerPage;
//   const indexOfFirstRow = indexOfLastRow - rowsPerPage;
//   const currentRows = filteredRefunds.slice(indexOfFirstRow, indexOfLastRow);

//   const handlePageChange = (page) => setCurrentPage(page);
//   const handleNextPage = () =>
//     setCurrentPage((prev) => Math.min(prev + 1, totalPages));
//   const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

//   const getVisiblePages = () => {
//     let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
//     let end = Math.min(totalPages, start + maxVisiblePages - 1);
//     if (end - start < maxVisiblePages - 1) {
//       start = Math.max(1, end - maxVisiblePages + 1);
//     }
//     return Array.from({ length: end - start + 1 }, (_, i) => start + i);
//   };

//   return (
//     <div className="p-6 bg-gray-50 min-h-screen flex flex-col">
//       <ToastContainer />
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">P M S</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() =>
//               navigate(
//                 "/dashboard/core-modules/property-management-system/front-desk-operations"
//               )
//             }
//             className="btn "
//           >
//             Front Desk Operations
//           </button>
//           <button
//             onClick={() =>
//               navigate(
//                 "/dashboard/core-modules/property-management-system/guest-managment"
//               )
//             }
//             className="btn "
//           >
//             Guest Management
//           </button>
//           <button
//             onClick={() =>
//               navigate(
//                 "/dashboard/core-modules/property-management-system/front-desk-roomservices"
//               )
//             }
//             className="btn"
//           >
//             Hospitality Services
//           </button>
//           <button
//             onClick={() =>
//               navigate(
//                 "/dashboard/core-modules/property-management-system/front-desk-amenities"
//               )
//             }
//             className="btn "
//           >
//             Facility Services
//           </button>
//           <button
//             onClick={() =>
//               navigate(
//                 "/dashboard/core-modules/property-management-system/billing-payments"
//               )
//             }
//             className="btn"
//           >
//             Billing & Payments
//           </button>
//           <button
//             onClick={() =>
//               navigate(
//                 "/dashboard/core-modules/property-management-system/refunds-adjustments"
//               )
//             }
//             className="btn  text-black font-bold"
//           >
//             Refunds & Adjustments
//           </button>
//         </div>
//       </header>

//       <div className="text-center mb-6">
//         <div className="inline-block">
//           <h1 className="text-2xl font-bold text-center text-black">
//             Refunds & Adjustments
//           </h1>
//           <h1 className="text-sm font-poppins font-semibold text-center text-gray-600">
//             Manage refunds and adjustments with detailed tracking and reporting
//           </h1>
//           <hr className="border-gray-400 mt-1 w-60 mx-auto mb-4" />
//         </div>
//       </div>

//       {/* Statistics Section */}
//       {statistics && (
//         <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
//           <div className="bg-white p-4 rounded-lg shadow">
//             <h3 className="text-sm font-medium text-gray-500">Total Refunds</h3>
//             <p className="text-2xl font-bold text-black">{statistics.total_refunds}</p>
//           </div>
//           <div className="bg-white p-4 rounded-lg shadow">
//             <h3 className="text-sm font-medium text-gray-500">Total Refund Amount</h3>
//             <p className="text-2xl font-bold text-green-600">₹{statistics.total_refund_amount?.toFixed(2) || "0.00"}</p>
//           </div>
//           <div className="bg-white p-4 rounded-lg shadow">
//             <h3 className="text-sm font-medium text-gray-500">Average Refund</h3>
//             <p className="text-2xl font-bold text-blue-600">₹{statistics.average_refund_amount?.toFixed(2) || "0.00"}</p>
//           </div>
//           <div className="bg-white p-4 rounded-lg shadow">
//             <h3 className="text-sm font-medium text-gray-500">Total Service Fee</h3>
//             <p className="text-2xl font-bold text-yellow-500">₹{statistics.total_service_fee?.toFixed(2) || "0.00"}</p>
//           </div>
//         </div>
//       )}

//       <div className="mb-4 flex flex-col md:flex-row items-center gap-4">
//         <div className="relative w-full md:w-1/3">
//           <input
//             type="text"
//             placeholder="Search by refund ID, guest name, or order number"
//             value={searchTerm}
//             onChange={handleSearch}
//             className="p-3 pl-12 h-12 border w-full border-gray-300 rounded-lg shadow-md"
//           />
//           <FiSearch className="absolute top-3 left-4 text-gray-500" size={20} />
//         </div>

//         <div className="flex flex-wrap gap-2 w-full md:w-auto justify-end">
//           <button
//             onClick={handleSearchRefunds}
//             className="px-4 py-3 h-12 bg-black text-white rounded-lg shadow-md hover:bg-gray-800 transition"
//           >
//             Search
//           </button>

//           <select
//             onChange={handleTimeRangeChange}
//             value={timeRange}
//             className="p-3 h-12 border rounded-lg shadow-md"
//           >
//             <option value="all">All Time</option>
//             <option value="day">Today</option>
//             <option value="week">This Week</option>
//             <option value="month">This Month</option>
//           </select>

//           <select
//             onChange={handleRefundStatusChange}
//             value={selectedRefundStatus}
//             className="p-3 h-12 border rounded-lg shadow-md"
//           >
//             <option value="all">All Status</option>
//             {refundStatuses.map((status, index) => (
//               <option key={index} value={status}>
//                 {status}
//               </option>
//             ))}
//           </select>
//         </div>
//       </div>

//       <div className="overflow-x-auto bg-white shadow-md rounded-md">
//         <table className="min-w-full bg-white border border-gray-300">
//           <thead>
//             <tr className="bg-gray-200 text-black font-semibold text-base">
//               <th className="py-3 px-2 border border-gray-300">Sr No.</th>
//               <th className="py-3 px-4 border border-gray-300">Refund ID</th>
//               <th className="py-3 px-4 border border-gray-300">Guest</th>
//               <th className="py-3 px-4 border border-gray-300">Order ID</th>
//               <th className="py-3 px-4 border border-gray-300">Refund Amount</th>
//               <th className="py-3 px-4 border border-gray-300">Final Amount</th>
//               <th className="py-3 px-4 border border-gray-300">Status</th>
//               <th className="py-3 px-4 border border-gray-300">Razorpay Refund ID</th>
//               <th className="py-3 px-4 border border-gray-300">Created Date</th>
//               <th className="py-3 px-2 border border-gray-300">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {currentRows.length > 0 ? (
//               currentRows.map((refund, index) => (
//                 <tr key={refund.refundid} className="hover:bg-gray-50 border">
//                   <td className="py-2 px-4 border text-center">
//                     {indexOfFirstRow + index + 1}
//                   </td>
//                   <td className="py-2 px-4 border text-sm font-medium">
//                     <span title={refund.refundid}>{maskId(refund.refundid)}</span>
//                   </td>
//                   <td className="py-2 px-4 border text-sm">
//                     {refund.booking_info?.guest_name || "N/A"}
//                   </td>
//                   <td className="py-2 px-4 border font-medium">
//                     {refund.booking_info?.orderid || "N/A"}
//                   </td>
//                   <td className="py-2 px-4 border font-medium">
//                     <span className="text-green-600 font-bold">
//                       ₹{parseFloat(refund.refund_amount || 0).toFixed(2)}
//                     </span>
//                   </td>
//                   <td className="py-2 px-4 border font-medium">
//                     ₹{parseFloat(refund.finalamount || 0).toFixed(2)}
//                   </td>
//                   <td className="py-2 px-4 border">
//                     <span
//                       className={`px-3 py-1 rounded-full text-xs ${getStatusBadge(refund.refund_status)}`}
//                     >
//                       {refund.refund_status || "Pending"}
//                     </span>
//                   </td>
//                   <td className="py-2 px-4 border">
//                     {refund.razorpay_refund_id ? (
//                       <span className="text-xs text-gray-600" title={refund.razorpay_refund_id}>
//                         {maskId(refund.razorpay_refund_id)}
//                       </span>
//                     ) : (
//                       <span className="text-xs text-gray-400">Not initiated</span>
//                     )}
//                   </td>
//                   <td className="py-2 px-4 border text-sm">
//                     {formatDate(refund.createdate)}
//                   </td>
//                   <td className="py-2 px-1 border">
//                     <div className="flex gap-2 justify-center">
//                       <button
//                         className="px-3 py-1 bg-black text-white rounded hover:bg-gray-700 transition text-sm flex items-center gap-1"
//                         onClick={() => handleViewDetails(refund)}
//                       >
//                         <FiSearch size={14} /> View
//                       </button>

//                       {(!refund.refund_status ||
//                         refund.refund_status === "Pending" ||
//                         refund.refund_status === "created" ||
//                         refund.refund_status === "No Refund Required") && (
//                         <>
//                           <button
//                             className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition text-sm flex items-center gap-1"
//                             onClick={() => handleEditRefund(refund)}
//                           >
//                             <FiEdit size={14} /> Edit
//                           </button>

//                           {canProcessRazorpayRefund(refund) ? (
//                             <button
//                               className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition text-sm flex items-center gap-1"
//                               onClick={() => handleRazorpayRefundClick(refund)}
//                             >
//                               <FiCreditCard size={14} /> Refund
//                             </button>
//                           ) : (
//                             <button
//                               className="px-3 py-1 bg-gray-300 text-gray-600 rounded cursor-not-allowed text-sm flex items-center gap-1"
//                               disabled
//                               title={refund.refund_status === "No Refund Required" ? "No refund required" : "Cannot process refund"}
//                             >
//                               <FiX size={14} /> Refund
//                             </button>
//                           )}
//                         </>
//                       )}
//                     </div>
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan="10" className="text-center py-8">
//                   <div className="text-gray-500">
//                     <p className="mb-2">No refund records found</p>
//                     <p className="text-sm">Try adjusting your search criteria</p>
//                   </div>
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       <div className="flex justify-center mt-8 text-xs gap-2">
//         <button
//           onClick={handlePrevPage}
//           disabled={currentPage === 1}
//           className={`px-3 py-1 rounded ${currentPage === 1
//             ? "bg-gray-300 cursor-not-allowed"
//             : "bg-black text-white hover:bg-gray-800"
//             }`}
//         >
//           Previous
//         </button>
//         {getVisiblePages().map((page) => (
//           <button
//             key={page}
//             onClick={() => handlePageChange(page)}
//             className={`px-3 py-2 mx-1 rounded-full ${currentPage === page
//               ? "bg-black text-white"
//               : "bg-gray-200 text-gray-700 hover:bg-gray-300"
//               }`}
//           >
//             {page}
//           </button>
//         ))}
//         <button
//           onClick={handleNextPage}
//           disabled={currentPage === totalPages}
//           className={`px-3 py-1 rounded ${currentPage === totalPages
//             ? "bg-gray-300 cursor-not-allowed"
//             : "bg-black text-white hover:bg-gray-800"
//             }`}
//         >
//           Next
//         </button>
//       </div>

//       {/* Refund Details Modal */}
//       {showDetailsModal && refundDetails && (
//         <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50 overflow-y-auto">
//           <div className="bg-white p-6 rounded-lg shadow-xl w-[90%] max-w-4xl max-h-[90vh] overflow-y-auto">
//             <div className="flex justify-between items-center mb-4">
//               <h2 className="text-xl text-black font-bold">Refund Details</h2>
//               <button
//                 onClick={() => setShowDetailsModal(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 <FiX size={24} />
//               </button>
//             </div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//               {/* Refund Information */}
//               <div className="space-y-4">
//                 <h3 className="font-bold text-lg border-b pb-2">Refund Information</h3>
//                 <div className="space-y-2">
//                   <div className="flex justify-between">
//                     <p className="text-gray-700 font-medium">Refund ID:</p>
//                     <p className="text-gray-700 font-mono" title={refundDetails.refundid}>
//                       {maskId(refundDetails.refundid)}
//                     </p>
//                   </div>
//                   <div className="flex justify-between">
//                     <p className="text-gray-700 font-medium">Billing ID:</p>
//                     <p className="text-gray-700" title={refundDetails.billingid}>
//                       {maskId(refundDetails.billingid)}
//                     </p>
//                   </div>
//                   <div className="flex justify-between">
//                     <p className="text-gray-700 font-medium">Status:</p>
//                     <span className={`px-3 py-1 rounded-full text-xs ${getStatusBadge(refundDetails.refund_status)}`}>
//                       {refundDetails.refund_status}
//                     </span>
//                   </div>
//                   <div className="flex justify-between">
//                     <p className="text-gray-700 font-medium">Razorpay Refund ID:</p>
//                     <p className="text-gray-700" title={refundDetails.razorpay_refund_id}>
//                       {refundDetails.razorpay_refund_id ? maskId(refundDetails.razorpay_refund_id) : "N/A"}
//                     </p>
//                   </div>
//                   <div className="flex justify-between">
//                     <p className="text-gray-700 font-medium">Refund Reason:</p>
//                     <p className="text-gray-700 text-right max-w-xs">{refundDetails.refund_reason || "N/A"}</p>
//                   </div>
//                 </div>
//               </div>

//               {/* Booking Information */}
//               {refundDetails.booking_details && (
//                 <div className="space-y-4">
//                   <h3 className="font-bold text-lg border-b pb-2">Reservation Information</h3>
//                   <div className="space-y-2">
//                     <div className="flex justify-between">
//                       <p className="text-gray-700 font-medium">Reservation Number:</p>
//                       <p className="text-gray-700">{refundDetails.booking_details.orderid}</p>
//                     </div>
//                     <div className="flex justify-between">
//                       <p className="text-gray-700 font-medium">Guest:</p>
//                       <p className="text-gray-700">{refundDetails.booking_details.guest_details?.guest_name || "N/A"}</p>
//                     </div>
//                     <div className="flex justify-between">
//                       <p className="text-gray-700 font-medium">Room Type:</p>
//                       <p className="text-gray-700">{refundDetails.booking_details.room_details?.roomtypename || "N/A"}</p>
//                     </div>
//                     <div className="flex justify-between">
//                       <p className="text-gray-700 font-medium">Check-in:</p>
//                       <p className="text-gray-700">{formatDate(refundDetails.booking_details.checkin_date)}</p>
//                     </div>
//                     <div className="flex justify-between">
//                       <p className="text-gray-700 font-medium">Check-out:</p>
//                       <p className="text-gray-700">{formatDate(refundDetails.booking_details.checkout_date)}</p>
//                     </div>
//                   </div>
//                 </div>
//               )}

//               {/* Amount Details */}
//               <div className="space-y-4">
//                 <h3 className="font-bold text-lg border-b pb-2">Amount Details</h3>
//                 <div className="space-y-2">
//                   <div className="flex justify-between">
//                     <p className="text-gray-700 font-medium">Refund Amount:</p>
//                     <p className=" font-bold text-green-600">₹{parseFloat(refundDetails.refund_amount || 0).toFixed(2)}</p>
//                   </div>
//                   <div className="flex justify-between">
//                     <p className="text-gray-700 font-medium">Service Fee:</p>
//                     <p className="text-gray-700">₹{parseFloat(refundDetails.service_fee || 0).toFixed(2)}</p>
//                   </div>
//                   <div className="flex justify-between border-t pt-2">
//                     <p className="text-gray-700 font-medium">Final Amount:</p>
//                     <p className="text-gray-700 font-bold text-lg">₹{parseFloat(refundDetails.finalamount || 0).toFixed(2)}</p>
//                   </div>
//                 </div>
//               </div>

//               {/* Amenities Summary */}
//               {refundDetails.amenities_summary && (
//                 <div className="space-y-4">
//                   <h3 className="font-bold text-lg border-b pb-2">Amenities Summary</h3>
//                   <div className="space-y-2">
//                     <div className="flex justify-between">
//                       <p className="text-gray-700 font-medium">Refundable Amenities:</p>
//                       <p className="text-green-600">₹{parseFloat(refundDetails.amenities_summary.refundable_amenities_total || 0).toFixed(2)}</p>
//                     </div>
//                     <div className="flex justify-between">
//                       <p className="text-gray-700 font-medium">Non-refundable Amenities:</p>
//                       <p className="text-red-600">₹{parseFloat(refundDetails.amenities_summary.non_refundable_amenities_total || 0).toFixed(2)}</p>
//                     </div>
//                     <div className="flex justify-between border-t pt-2">
//                       <p className="text-gray-700 font-medium">Total Amenities:</p>
//                       <p className="text-gray-700 font-bold">₹{parseFloat(refundDetails.amenities_summary.total_amenities_amount || 0).toFixed(2)}</p>
//                     </div>
//                   </div>
//                 </div>
//               )}
//             </div>

//             {/* Payment Details */}
//             {refundDetails.payment_details && refundDetails.payment_details.length > 0 && (
//               <div className="mt-6">
//                 <h3 className="font-bold text-lg border-b pb-2 mb-4">Payment Details</h3>
//                 <div className="overflow-x-auto">
//                   <table className="min-w-full border border-gray-300">
//                     <thead>
//                       <tr className="bg-gray-100">
//                         <th className="py-2 px-4 border">Transaction ID</th>
//                         <th className="py-2 px-4 border">Amount</th>
//                         <th className="py-2 px-4 border">Status</th>
//                         <th className="py-2 px-4 border">Method</th>
//                         <th className="py-2 px-4 border">Date</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {refundDetails.payment_details.map((payment, idx) => (
//                         <tr key={idx}>
//                           <td className="py-2 px-4 border text-sm" title={payment.transactionid}>
//                             {maskId(payment.transactionid)}
//                           </td>
//                           <td className="py-2 px-4 border">₹{parseFloat(payment.finalamount || 0).toFixed(2)}</td>
//                           <td className="py-2 px-4 border">
//                             <span className={`px-2 py-1 rounded text-xs ${payment.paymentstatus === "Success" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
//                               {payment.paymentstatus}
//                             </span>
//                           </td>
//                           <td className="py-2 px-4 border">{payment.paymentmethod}</td>
//                           <td className="py-2 px-4 border text-sm">{formatDateTime(payment.created_at)}</td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>
//               </div>
//             )}

//             <div className="flex justify-end mt-6">
//               <button
//                 className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
//                 onClick={() => setShowDetailsModal(false)}
//               >
//                 Close
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Update Refund Modal */}
//       {showUpdateModal && selectedRefund && (
//         <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
//           <div className="bg-white p-6 rounded-lg shadow-xl w-[470px]">
//             <h2 className="text-xl text-black font-bold mb-4">
//               Update Refund Details
//             </h2>

//             <div className="space-y-4 mb-6">
//               <div>
//                 <label className="block text-gray-700 text-sm font-bold mb-2">
//                   Refund Amount
//                 </label>
//                 <input
//                   type="number"
//                   step="0.01"
//                   value={updatedRefundData.refund_amount}
//                   onChange={(e) => setUpdatedRefundData({...updatedRefundData, refund_amount: e.target.value})}
//                   className="w-full p-2 border rounded"
//                   placeholder="Enter refund amount"
//                 />
//               </div>

//               <div>
//                 <label className="block text-gray-700 text-sm font-bold mb-2">
//                   Service Fee
//                 </label>
//                 <input
//                   type="number"
//                   step="0.01"
//                   value={updatedRefundData.service_fee}
//                   onChange={(e) => setUpdatedRefundData({...updatedRefundData, service_fee: e.target.value})}
//                   className="w-full p-2 border rounded"
//                   placeholder="Enter service fee"
//                 />
//               </div>

//               <div>
//                 <label className="block text-gray-700 text-sm font-bold mb-2">
//                   Refund Reason
//                 </label>
//                 <textarea
//                   value={updatedRefundData.refund_reason}
//                   onChange={(e) => setUpdatedRefundData({...updatedRefundData, refund_reason: e.target.value})}
//                   className="w-full p-2 border rounded"
//                   rows="3"
//                   placeholder="Enter refund reason"
//                 />
//               </div>

//               <div>
//                 <label className="block text-gray-700 text-sm font-bold mb-2">
//                   Admin Notes
//                 </label>
//                 <textarea
//                   value={updatedRefundData.admin_notes}
//                   onChange={(e) => setUpdatedRefundData({...updatedRefundData, admin_notes: e.target.value})}
//                   className="w-full p-2 border rounded"
//                   rows="2"
//                   placeholder="Additional notes (optional)"
//                 />
//               </div>
//             </div>

//             <div className="flex justify-end gap-3">
//               <button
//                 className="px-4 py-2 bg-red-500 text-white rounded-lg hover:text-white transition"
//                 onClick={() => setShowUpdateModal(false)}
//               >
//                 Cancel
//               </button>
//               <button
//                 className="px-4 py-2 bg-green-500 text-white rounded-lg hover:text-white transition"
//                 onClick={handleUpdateRefund}
//               >
//                 Update Refund
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Razorpay Refund Confirmation Modal */}
//       {showConfirmModal && selectedRefund && (
//         <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
//           <div className="bg-white p-6 rounded-lg shadow-xl w-[500px]">
//             <div className="flex items-center mb-4">
//               <FiCreditCard className="text-green-500 mr-3" size={24} />
//               <h2 className="text-xl text-black font-bold">Confirm Refund</h2>
//             </div>

//             <div className="space-y-4 mb-6 bg-gray-50 p-4 rounded-lg">
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <p className="text-gray-700 font-medium text-sm">Refund ID:</p>
//                   <p className="text-gray-900 font-mono text-sm" title={selectedRefund.refundid}>
//                     {maskId(selectedRefund.refundid)}
//                   </p>
//                 </div>
//                 <div>
//                   <p className="text-gray-700 font-medium text-sm">Reservation Number:</p>
//                   <p className="text-gray-900 font-medium">{selectedRefund.booking_info?.orderid || "N/A"}</p>
//                 </div>
//               </div>

//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <p className="text-gray-700 font-medium text-sm">Guest:</p>
//                   <p className="text-gray-900">{selectedRefund.booking_info?.guest_name || "N/A"}</p>
//                 </div>
//                 <div>
//                   <p className="text-gray-700 font-medium text-sm">Phone:</p>
//                   {selectedRefund.booking_info?.guest_phone ||
//                selectedRefund.booking_details?.guest_details?.phonenumber ||
//                "N/A"}
//                 </div>
//               </div>

//               <div className="border-t pt-4">
//                 <div className="flex justify-between items-center">
//                   <div>
//                     <p className="text-gray-700 font-medium">Refund Amount:</p>
//                     <p className="text-gray-500 text-xs">Amount to be refunded from refund_amount</p>
//                   </div>
//                   <p className="text-2xl font-bold text-green-600">
//                     ₹{parseFloat(selectedRefund.refund_amount || 0).toFixed(2)}
//                   </p>
//                 </div>

//                 {selectedRefund.service_fee > 0 && (
//                   <div className="flex justify-between mt-2 text-sm">
//                     <p className="text-gray-600">Service Fee (not refundable):</p>
//                     <p className="text-red-600">-₹{parseFloat(selectedRefund.service_fee || 0).toFixed(2)}</p>
//                   </div>
//                 )}

//                 <div className="flex justify-between mt-3 pt-3 border-t text-sm">
//                   <p className="text-gray-700 font-medium">Final Amount (for reference):</p>
//                   <p className="font-bold text-black">
//                     ₹{parseFloat(selectedRefund.finalamount || 0).toFixed(2)}
//                   </p>
//                 </div>
//               </div>

//               {/* Payment Method Info */}
//               {selectedRefund.payment_details && selectedRefund.payment_details.length > 0 && (
//                 <div className="mt-3 p-3 bg-blue-50 rounded-lg">
//                   <p className="text-xs text-blue-800 font-medium">Original Payment Method:</p>
//                   <p className="text-sm mt-1">
//                     {selectedRefund.payment_details[0].paymentmethod || "Unknown"}
//                     {selectedRefund.payment_details[0].paymentgateway && ` via ${selectedRefund.payment_details[0].paymentgateway}`}
//                   </p>
//                 </div>
//               )}
//             </div>

//             {/* Admin Notes (Optional) */}
//             <div className="mb-6">
//               <label className="block text-gray-700 text-sm font-medium mb-2">
//                 Admin Notes (Optional)
//               </label>
//               <textarea
//                 value={updatedRefundData.admin_notes || ""}
//                 onChange={(e) => setUpdatedRefundData({...updatedRefundData, admin_notes: e.target.value})}
//                 className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
//                 rows="2"
//                 placeholder="Add any internal notes about this refund..."
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 Notes are for internal use only and won't be shared with the guest
//               </p>
//             </div>

//             <div className="flex justify-end gap-3">
//               <button
//                 className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition font-medium"
//                 onClick={() => setShowConfirmModal(false)}
//                 disabled={processing}
//               >
//                 Cancel
//               </button>
//               <button
//                 className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
//                 onClick={handleInitiateRefund}
//                 disabled={processing}
//               >
//                 {processing ? (
//                   <>
//                     <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
//                     Processing...
//                   </>
//                 ) : (
//                   <>
//                     <FiCreditCard size={16} />
//                     Process Refund (₹{parseFloat(selectedRefund.refund_amount || 0).toFixed(2)})
//                   </>
//                 )}
//               </button>
//             </div>

//             {/* Refund Timing Info */}
//             <div className="mt-4 text-xs text-gray-500 border-t pt-3">
//               <p>• Refund will be processed to the original payment method</p>
//               <p>• Guest will receive confirmation from Razorpay via SMS/email</p>
//               <p>• Bank transfers may take 3-5 business days, UPI/wallet refunds are usually instant</p>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default RefundAdjustments;
